/**
 * Cálculo de arrendamiento puro basado en "COTIZADOR ARRENDAMIENTO 2026.xlsx" (hoja Lease Schedule).
 * Todos los porcentajes se reciben en unidades de porcentaje (40 = 40%).
 */
/** Valores generales del Excel de arrendamiento */
export const LEASING_DEFAULTS = {
  annualRate: 40,
  ivaPercent: 16,
  commissionPercent: 2,
  residualPercent: 0.05,
  annualInsurance: 4566.14,
};

export type LeasingQuoteParams = {
  /** Valor total de la inversión IVA incluido (precio de la moto) */
  equipmentValue: number;
  /** Plazo en meses */
  termMonths: number;
  /** Tasa anual % */
  annualRate: number;
  /** Tasa IVA % */
  ivaPercent?: number;
  /** Comisión por apertura % */
  commissionPercent?: number;
  /** Pago anticipado % (sobre el valor antes de IVA) */
  advancePercent?: number;
  /** Valor residual % (sobre el valor antes de IVA) */
  residualPercent?: number;
  /** Seguros anuales antes de IVA */
  annualInsurance?: number;
  /** Accesorios financiados */
  accessories?: number;
  /** Rentas en garantía (monto) */
  guaranteeDeposit?: number;
  /** true = seguros prepagados (no se financian) */
  prepaidInsurance?: boolean;
};

export type LeasingQuoteResult = {
  netValue: number;
  financedInsurance: number;
  advancePayment: number;
  residualValue: number;
  residualNpv: number;
  leaseValue: number;
  /** Pago por periodo antes de IVA */
  monthlyPayment: number;
  /** Pago por periodo IVA incluido */
  monthlyPaymentWithIva: number;
  commission: number;
  /** Monto requerido a la firma */
  initialPayment: number;
};

const pmt = (rate: number, periods: number, presentValue: number) => {
  if (periods <= 0) return 0;
  if (rate === 0) return presentValue / periods;
  return (presentValue * rate) / (1 - (1 + rate) ** -periods);
};

export const calculateLeasingQuote = ({
  equipmentValue,
  termMonths,
  annualRate,
  ivaPercent = 16,
  commissionPercent = 0,
  advancePercent = 0,
  residualPercent = 0,
  annualInsurance = 0,
  accessories = 0,
  guaranteeDeposit = 0,
  prepaidInsurance = false,
}: LeasingQuoteParams): LeasingQuoteResult => {
  const iva = ivaPercent / 100;
  const monthlyRate = annualRate / 100 / 12;
  const years = termMonths / 12;

  const netValue = equipmentValue / (1 + iva);
  const financedInsurance = annualInsurance * years;
  const advancePayment = (advancePercent / 100) * netValue;
  const residualValue = Math.round(netValue * (residualPercent / 100));
  const residualNpv = residualValue / (1 + monthlyRate) ** termMonths;

  const leaseValue =
    netValue + accessories + (prepaidInsurance ? 0 : financedInsurance) - advancePayment - residualNpv;

  const monthlyPayment = leaseValue > 0 ? pmt(monthlyRate, termMonths, leaseValue) : 0;
  const monthlyPaymentWithIva = monthlyPayment * (1 + iva);
  const commission = Math.round(leaseValue * (commissionPercent / 100) * (1 + iva));
  const initialPayment = guaranteeDeposit + advancePayment + commission;

  return {
    netValue,
    financedInsurance,
    advancePayment,
    residualValue,
    residualNpv,
    leaseValue,
    monthlyPayment,
    monthlyPaymentWithIva,
    commission,
    initialPayment,
  };
};

export type CreditQuoteParams = {
  /** Valor de la unidad */
  unitValue: number;
  /** Plazo en meses */
  termMonths: number;
  /** Tasa anual % sin IVA */
  annualRate: number;
  /** Tasa IVA % */
  ivaPercent?: number;
  /** Comisión por apertura % */
  commissionPercent?: number;
  /** Enganche % */
  downPaymentPercent?: number;
  /** Seguro financiado */
  insurance?: number;
};

export type CreditQuoteResult = {
  downPayment: number;
  financedAmount: number;
  /** Comisión IVA incluido */
  commission: number;
  /** Enganche + comisión */
  initialPayment: number;
  /** Pago mensual IVA de intereses incluido */
  monthlyPayment: number;
};

/**
 * Cálculo de crédito basado en "COTIZADOR CREDITO 2026.xlsx" (hoja TABLA DE PAGOS).
 * pago = PMT(tasa anual / 12 × (1 + IVA), meses, monto a financiar)
 */
export const calculateCreditQuote = ({
  unitValue,
  termMonths,
  annualRate,
  ivaPercent = 16,
  commissionPercent = 0,
  downPaymentPercent = 0,
  insurance = 0,
}: CreditQuoteParams): CreditQuoteResult => {
  const iva = ivaPercent / 100;
  const monthlyRate = (annualRate / 100 / 12) * (1 + iva);

  const total = unitValue + insurance;
  const downPayment = total * (downPaymentPercent / 100);
  const financedAmount = Math.max(total - downPayment, 0);
  const commission = financedAmount * (commissionPercent / 100) * (1 + iva);
  const initialPayment = downPayment + commission;
  const monthlyPayment = financedAmount > 0 ? pmt(monthlyRate, termMonths, financedAmount) : 0;

  return { downPayment, financedAmount, commission, initialPayment, monthlyPayment };
};
